import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  Typography,
  IconButton,
  Paper,
  CircularProgress,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SendIcon from "@mui/icons-material/Send";
import ClearIcon from "@mui/icons-material/Clear";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import { getAllAIAgents, chatWithAgent } from "../../api";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { useNavigate } from "react-router-dom";


const AIAgentChat = ({ appId }) => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [messages, setMessages] = useState({});
  const [currentChat, setCurrentChat] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch AI Agents
  useEffect(() => {
    getAllAIAgents(appId)
      .then((res) => {
        setAgents(res.data.data);
        setSelectedAgent(res.data.data[0]);
      })
      .catch((err) => console.error("Error fetching AI Agents:", err));
  }, [appId]);

  // Handle Agent Selection
  const handleSelectAgent = (agent) => {
    setSelectedAgent(agent);
    setCurrentChat(messages[agent._id] || []);
  };

  // Handle Sending Query
  const handleSendQuery = () => {
    if (!query.trim() || !selectedAgent) return;

    setLoading(true);
    const newMessage = { role: "user", content: query };
    const updatedChat = [...(messages[selectedAgent._id] || []), newMessage];
    setMessages({ ...messages, [selectedAgent._id]: updatedChat });
    setCurrentChat(updatedChat);
    setQuery("");
    setSuggestions([]);

    // Send Query to Agent
    chatWithAgent(selectedAgent._id, updatedChat)
      .then((res) => {
        const agentResponse = { role: "assistant", content: res.data.reply.content };
        if (res.data.reply.suggestions) {
          setSuggestions(res.data.reply.suggestions);
        } else {
          setSuggestions([]);
        }
        const updatedChatWithResponse = [...updatedChat, agentResponse];
        setMessages({
          ...messages,
          [selectedAgent._id]: updatedChatWithResponse,
        });
        setCurrentChat(updatedChatWithResponse);
      })
      .catch((err) => console.error("Error chatting with agent:", err))
      .finally(() => setLoading(false));
  };

  // Handle Clear Chat
  const handleClearChat = () => {
    setMessages({ ...messages, [selectedAgent._id]: [] });
    setCurrentChat([]);
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* Left Panel: Agent List */}
      <Box sx={{ width: "25%", borderRight: "1px solid #ccc", p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          AI Agents
        </Typography>
        <List>
          {agents.map((agent) => (
            <ListItem
              key={agent._id}
              button
              selected={selectedAgent?._id === agent._id}
              onClick={() => handleSelectAgent(agent)}
              sx={{
                border: selectedAgent?._id === agent._id ? "2px solid #1976d2" : "1px solid #ccc",
                backgroundColor: selectedAgent?._id === agent._id ? "#e3f2fd" : "inherit",
                borderRadius: "8px",
                mb: 1,
                transition: "background-color 0.3s, border 0.3s",
              }}
            >
              <ListItemText
                primary={agent.agentName}
                primaryTypographyProps={{
                  fontWeight: selectedAgent?._id === agent._id ? "bold" : "normal",
                }}
              />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/agents/edit/${agent._id}`);
                }}
                sx={{ ml: 1 }}
              >
                <EditIcon sx={{ color: "#1976d2" }} />
              </IconButton>
              {selectedAgent?._id === agent._id && (
                <ChevronRightIcon sx={{ color: "#1976d2" }} />
              )}
            </ListItem>
          ))}
        </List>
        <Button
          startIcon={<AddCircleOutlineIcon />}
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 2 }}
          onClick={() => navigate(`/agents/new`)}
        >
          Add New Agent
        </Button>
      </Box>

      {/* Right Panel: Chat */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* Chat Display */}
        <Box sx={{ flexGrow: 1, p: 2, overflowY: "auto", backgroundColor: "#f9f9f9" }}>
          {currentChat.map((msg, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                mb: 1,
              }}
            >
              <Paper
                sx={{
                  p: 1,
                  backgroundColor: msg.role === "user" ? "#d1e7dd" : "#f8d7da",
                  maxWidth: "60%",
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkBreaks]}>{msg.content}</ReactMarkdown>
              </Paper>
            </Box>
          ))}
          {/* Suggestions Section */}
          {suggestions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {suggestions.map((suggestion, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    size="small"
                    sx={{
                      textTransform: "none",
                      backgroundColor: "#e3f2fd",
                      borderColor: "#1976d2",
                      color: "#1976d2",
                      ":hover": { backgroundColor: "#bbdefb" },
                    }}
                    onClick={() => {
                      setQuery(suggestion);
                      setTimeout(() => handleSendQuery(), 0);
                    }}
                  >
                    {suggestion}
                  </Button>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Query Input */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #ccc",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            placeholder="Type your query..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSendQuery()}
          />
          <IconButton
            color="primary"
            onClick={handleSendQuery}
            disabled={loading}
            sx={{ flexShrink: 0 }}
          >
            {loading ? <CircularProgress size={24} /> : <SendIcon />}
          </IconButton>
          <IconButton
            color="secondary"
            onClick={handleClearChat}
            sx={{ flexShrink: 0 }}
          >
            <ClearIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default AIAgentChat;
